/**
 * GradeTrajectoryStrip — chronological 5-box (or N-box) trajectory of the
 * aggregated worst-of grade per quarter.
 *
 * §5.BT §3.3 P3 Commit C (2026-06-19). Locked in
 *   docs/audits/2026-06-18_5bt_p3_3_dossier_p2_design.md §D.4 + Consult #7
 *   separate observation: vendor with N quarters renders N boxes — NOT 5
 *   padded with empty placeholders.
 *
 * Reads quarter blocks from the endpoint response (DESC). Renders chronologically
 * (oldest → newest, left → right). When fewer than the requested-window of
 * quarters exist, renders only what's present — no padding.
 *
 * Grade colors via gradeColors.js — F-grade uses the new dangerDeep token.
 */
import React from 'react';
import { getGradeColor } from './gradeColors';

// Lane 215 (Linear rebuild, Stage 2 — dossier child chrome): read the `.sd`
// Linear token layer so the strip flips with the shell in the future dark
// lane. The getGradeColor grade boxes are untouched.
const LINEAR_PANEL = {
  background: 'var(--panel)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  boxShadow: 'var(--shadow)',
};

function GradeBox({ label, grade }) {
  const style = getGradeColor(grade);
  return (
    <div
      data-testid="trajectory-box"
      data-grade={grade || 'NONE'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px',
        flex: '1 1 0',
        minWidth: 0,
      }}
    >
      <div
        style={{
          width: '100%',
          aspectRatio: '1',
          maxWidth: '64px',
          borderRadius: '10px',
          backgroundColor: style.bg,
          color: style.fg,
          border: `1px solid ${style.fg}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-bold)',
          lineHeight: 1,
        }}
      >
        {grade || 'Not graded'}
      </div>
      <div
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-3)',
          whiteSpace: 'nowrap',
          textAlign: 'center',
        }}
      >
        {label}
      </div>
    </div>
  );
}

/**
 * GradeTrajectoryStrip. Props:
 *   - quarters : array of quarter blocks (DESC order; backend contract).
 */
function GradeTrajectoryStrip({ quarters }) {
  const list = quarters || [];
  if (list.length === 0) return null;
  const chronological = [...list].reverse();

  return (
    <div style={{ ...LINEAR_PANEL, padding: '16px 20px', marginBottom: '24px' }}>
      <div
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--text-3)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--letter-spacing-wide)',
          marginBottom: '12px',
        }}
      >
        Grade Trajectory
      </div>
      <div
        style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
        }}
        data-testid="trajectory-strip"
      >
        {chronological.map((q, idx) => (
          <GradeBox
            key={`${q.period_start || idx}-${idx}`}
            label={q.period_label || ''}
            grade={(q.aggregated && q.aggregated.overall_rating) || null}
          />
        ))}
      </div>
    </div>
  );
}

export default GradeTrajectoryStrip;
